import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  NavigationDashboardTabItemDto,
  NavigationDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  AsyncNotification,
  Button,
  Input,
  Modal,
  Textarea,
} from '@asset-metrix/portal-ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  createDashboardAndPinToNavigation,
  invalidateDashboardListQueries,
  isCreateDashboardPinFailedError,
} from '../../api/portalGateway/dashboard';
import { applyDashboardNavCacheChange } from '../../api/portalGateway/navigation';
import { useDashboardEditModeStore } from '../../store/states/dashboardEditMode';

import styles from './dashboard-modals.module.scss';

type CreateDashboardFormValues = {
  name: string;
  description: string | null;
};

type CreateDashboardModalProps = {
  opened: boolean;
  sourcePageId: string;
  onClose: () => void;
  onCreated: (dashboardSlug: string) => void;
};

export function findDashboardSlug(
  items: readonly NavigationDashboardTabItemDto[],
  dashboardId: string
): string | undefined {
  return items.find((item) => item.dashboardId === dashboardId)?.slug;
}

export function findDashboardSlugForPage(
  navigation: NavigationDto,
  sourcePageId: string,
  dashboardId: string
): string | undefined {
  return findDashboardSlug(
    navigation.dashboardNavItems[sourcePageId] ?? [],
    dashboardId
  );
}

export function CreateDashboardModal({
  opened,
  sourcePageId,
  onClose,
  onCreated,
}: CreateDashboardModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (!opened) {
      setName('');
      setDescription('');
    }
  }, [opened]);

  const mutation = useMutation({
    mutationFn: (values: CreateDashboardFormValues) =>
      createDashboardAndPinToNavigation(sourcePageId, values),
    onSuccess: async (instance, values) => {
      const nextItems = applyDashboardNavCacheChange(
        queryClient,
        sourcePageId,
        {
          op: 'add',
          instanceId: instance.id,
          dashboardId: instance.dashboardId,
          order: instance.order,
          label: values.name.trim(),
        }
      );
      await invalidateDashboardListQueries(queryClient, sourcePageId);
      const dashboardSlug =
        findDashboardSlug(nextItems, instance.dashboardId) ??
        instance.dashboardId;
      useDashboardEditModeStore
        .getState()
        .requestOpenInEdit(instance.dashboardId);
      onCreated(dashboardSlug);
    },
    onError: (error) => {
      const message = isCreateDashboardPinFailedError(error)
        ? t('app.dashboard.createModal.createInstanceFailed')
        : t('app.dashboard.createModal.submitFailed');
      AsyncNotification.show({ message }, 'error');
    },
  });

  const handleSubmit = () => {
    mutation.mutate({
      name: name.trim(),
      description: description.trim() || null,
    });
  };

  return (
    <Modal
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('app.dashboard.createModal.cancel')}
          </Button>
          <Button
            disabled={name.trim().length === 0 || mutation.isPending}
            loading={mutation.isPending}
            onClick={handleSubmit}
          >
            {t('app.dashboard.createModal.submit')}
          </Button>
        </>
      }
      data-testid="create-dashboard-modal"
      modalType="action"
      onClose={onClose}
      opened={opened}
      title={t('app.dashboard.createModal.title')}
    >
      <div className={styles.createModalForm}>
        <Input
          classNames={{ root: styles.createModalFieldInner }}
          label={t('app.dashboard.createModal.nameLabel')}
          required
          value={name}
          onChange={setName}
        />
        <div className={styles.createModalTextarea}>
          <Textarea
            label={t('app.dashboard.createModal.descriptionLabel')}
            value={description}
            onChange={setDescription}
          />
        </div>
      </div>
    </Modal>
  );
}
